import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Event } from './sportsAndEventsInterface';

export interface EventSearchState {
  // Event search results from API
  allEvents: Event[];
  filteredEvents: Event[];

  // Pagination state
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalEvents: number;

  // Search and filter states
  isLoading: boolean;
  error: string | null;

  // UI states
  selectedEventId: string | null;
  viewMode: 'grid' | 'list';
}

export  const ITEMS_PER_PAGE = 30;

const initialState: EventSearchState = {
  allEvents: [],
  filteredEvents: [],
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: ITEMS_PER_PAGE,
  totalEvents: 0,
  isLoading: false,
  error: null,
  selectedEventId: null,
  viewMode: 'grid',
};

export const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    // Set all events from API response
    setEvents: (state, action: PayloadAction<Event[]>) => {
      state.allEvents = action.payload;
      state.filteredEvents = action.payload;
      state.totalEvents = action.payload?.length;
      state.totalPages = Math.ceil(action.payload?.length / state.itemsPerPage);
      state.currentPage = 1; // Reset to first page when new data arrives
      state.error = null;
    },

    // Set filtered events (after client-side filtering)
    setFilteredEvents: (state, action: PayloadAction<Event[]>) => {
      state.filteredEvents = action.payload;
      state.totalEvents = action.payload?.length;
      state.totalPages = Math.ceil(action.payload?.length / state.itemsPerPage);
      state.currentPage = 1; // Reset to first page when filters change
    },

    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.totalPages = Math.ceil(state.totalEvents / action.payload);
      state.currentPage = 1; // Reset to first page when changing items per page
    },

    // UI state actions
    setSelectedEvent: (state, action: PayloadAction<string | null>) => {
      state.selectedEventId = action.payload;
    },

    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null; // Clear error when starting new request
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Reset all data
    resetEventState: (state) => {
      return initialState;
    },
  },
});

export const {
  setEvents,
  setFilteredEvents,
  setCurrentPage,
  setItemsPerPage,
  setSelectedEvent,
  setViewMode,
  setLoading,
  setError,
  clearError,
  resetEventState,
} = eventSlice.actions;

// Selectors for easy access to state
export const selectAllEvents = (state: { events: EventSearchState }) =>
  state.events.allEvents;
export const selectFilteredEvents = (state: { events: EventSearchState }) =>
  state.events.filteredEvents;
export const selectCurrentPage = (state: { events: EventSearchState }) =>
  state.events.currentPage;
export const selectTotalPages = (state: { events: EventSearchState }) =>
  state.events.totalPages;
export const selectItemsPerPage = (state: { events: EventSearchState }) =>
  state.events.itemsPerPage;
export const selectTotalEvents = (state: { events: EventSearchState }) =>
  state.events.totalEvents;
export const selectIsLoading = (state: { events: EventSearchState }) =>
  state.events.isLoading;
export const selectError = (state: { events: EventSearchState }) =>
  state.events.error;
export const selectSelectedEventId = (state: { events: EventSearchState }) =>
  state.events.selectedEventId;
export const selectViewMode = (state: { events: EventSearchState }) =>
  state.events.viewMode;

// Computed selectors
export const selectPaginatedEvents = (state: { events: EventSearchState }) => {
  const { filteredEvents, currentPage, itemsPerPage } = state.events;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filteredEvents.slice(startIndex, endIndex);
};

export const selectSelectedEvent = (state: { events: EventSearchState }) => {
  const { filteredEvents, selectedEventId } = state.events;
  return (
    filteredEvents.find((event) => event.identifier === selectedEventId) || null
  );
};

export default eventSlice.reducer;

